import React, { FC, ReactElement, useEffect, useState } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';
import mustache from 'mustache';
import { useSettings } from '../../../hooks/useSettings';
import { useNonce } from '../../../lib/nonceContext';
import { stampScriptNonce } from '../../../lib/stampScriptNonce';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import useOrderTracking from '../../../hooks/useOrderTracking';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import { MediumHeader } from '../../ui/Header/Header';
import TextLink, { TextLinkTargetType } from '../../ui/TextLink/TextLink';
import Alert from '../../ui/Alert/Alert';
import themeSettingsQuery from '../../../../../graphql/themeSettingsQuery';
import styles from './BroadcastBanner.module.css';
import {
  GetRetailerThemeQueryVariables,
  GetRetailerThemeQuery,
} from '../../../../../__generated__/loma';

const localStorage = (() => {
  if (typeof window === 'undefined') return undefined;
  try {
    // reading the localStorage property can throw if we're in an iframe
    // see: https://stackoverflow.com/questions/67645164
    return window.localStorage;
  } catch (err) {
    console.error('Error reading localStorage', err);
    return undefined;
  }
})();

export interface BlockBroadcastBannerProps {
  featureName?: string;
}

const BlockBroadcastBanner: FC<BlockBroadcastBannerProps> = ({
  featureName,
}): ReactElement | null => {
  const {
    state: pageContext,
    state: {
      retailerMoniker,
      versionName,
      localeLanguage,
      localeCountry,
      bannerName,
      cache,
    },
  } = usePageContext();

  const { broadcastBanner } = useSettings();
  const nonce = useNonce();

  const { data } = useSuspenseQuery<
    GetRetailerThemeQuery,
    GetRetailerThemeQueryVariables
  >(themeSettingsQuery, {
    variables: {
      retailerMoniker,
      versionName,
      localeLanguage,
      localeCountry,
      bannerName,
      featureName,
      cache,
    },
  });

  const {
    alertNoneNone,
    bodytextDefaultNone,
    headersMediumNone,
    linksTrackingDefault: linkStyle,
    linksTrackingHover: linkHoverStyle,
  } = data.retailerTheme;

  const {
    uid,
    startDateUtc: startDate,
    endDateUtc: endDate,
    title: messageTitle,
    body: messageBody,
    linkText,
    linkUrl,
    dismissible: dismissibleIn,
  } = broadcastBanner || {};
  const dismissible =
    dismissibleIn === null || dismissibleIn === undefined || dismissibleIn;

  const { getCurrentShipment } = useOrderTracking();
  const { trackingNumber = '', trackingUrl = '' } = getCurrentShipment() ?? {};

  const localStorageKey = uid
    ? `broadcastBanner_${uid}`
    : `broadcastBanner_${retailerMoniker}_${startDate ?? ''}${endDate ?? ''}`;

  const [visible, setVisible] = useState<boolean>(false);

  useEffect(
    () => {
      if (localStorage) {
        try {
          const broadcastBannerStorage = localStorage.getItem(localStorageKey);
          setVisible(!broadcastBannerStorage);
        } catch (err) {
          console.error('Error getting localStorage', err);
          setVisible(true);
        }
      }
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const logEvent = useAnalyticsPost();
  const baseLogInfo = {
    eventComponent: NarvarEventComponents.NTH_BLOCK_BROADCAST_BANNER,
    eventFeature: NarvarEventComponents.NTH_BLOCK_BROADCAST_BANNER,
    feature: featureName,
    pageComponent: NarvarEventComponents.NTH_BLOCK_BROADCAST_BANNER,
  };

  useEffect(
    () => {
      if (!messageBody && !messageTitle && !linkText) return;

      ({
        ...baseLogInfo,
        clickAssetId: uid,
        clickAssetSchdEndDateUtc: endDate,
        clickAssetSchdStartDateUtc: startDate,
        eventType: NarvarEventTypes.COMPONENT_RENDER,
      });
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageTitle, messageBody, linkText],
  );

  if (!messageBody && !messageTitle && !linkText) return null;
  const now = new Date();
  // date is null means "Start it Right Away" for start date and "Never Ending" for end date
  if (startDate && new Date(startDate) >= now) return null;
  if (endDate && new Date(endDate) <= now) return null;

  const bannerMessageTemplateOptions = {
    ...pageContext,
    trackingUrl,
    trackingNumber,
  };

  // Retailer banner HTML is rendered by sutro, so any <script> it carries
  // gets the request's CSP nonce — without it they'd be blocked once
  // script-src is enforced. Stamped BEFORE mustache on purpose: a <script>
  // materialized from a template variable (request-derived data) stays
  // un-nonced and is blocked under enforcement.
  const messageBodyRendered = mustache.render(
    stampScriptNonce(messageBody || '', nonce),
    bannerMessageTemplateOptions,
  );

  const onDismissClicked = (): void => {
    if (localStorage) {
      try {
        localStorage.setItem(localStorageKey, 'true');
      } catch (err) {
        console.error('Error setting localStorage', err);
      }
    }
    setVisible(false);

    logEvent({
      ...baseLogInfo,
      clickAssetId: uid,
      clickCategory: NarvarClickSubTypes.DISMISS,
      clickSubType: NarvarClickSubTypes.DISMISS,
      clickType: NarvarClickTypes.INTERACTION,
      eventType: NarvarEventTypes.CLICK,
    });
  };

  return (
    <Alert
      show={visible}
      dismissible={dismissible}
      dismissOnClick={onDismissClicked}
      data-test="broadcastBannerFeature"
      styleAlertOverrides={alertNoneNone}
    >
      {messageTitle && (
        <MediumHeader as="h2" headerCustomStyle={headersMediumNone}>
          {messageTitle}
        </MediumHeader>
      )}
      {messageBody && (
        <div
          className={styles.bannerText}
          style={bodytextDefaultNone}
          dangerouslySetInnerHTML={{ __html: messageBodyRendered }}
        />
      )}
      {linkText && (
        <TextLink
          target={TextLinkTargetType.BLANK}
          linkStyle={{ sDefault: linkStyle, sHover: linkHoverStyle }}
          href={linkUrl || ''}
          onClick={(): void =>
            logEvent({
              ...baseLogInfo,
              clickAssetId: uid,
              clickCategory: NarvarClickSubTypes.EXTERNAL_LINK,
              clickLinkDestinationUrl: linkUrl,
              clickLinkLabel: linkText,
              clickSubType: NarvarClickSubTypes.EXTERNAL_LINK,
              clickType: NarvarClickTypes.OUTGOING,
              eventType: NarvarEventTypes.CLICK,
            })
          }
        >
          {linkText}
        </TextLink>
      )}
    </Alert>
  );
};

export default BlockBroadcastBanner;
